import type { AutoReplyDto } from '@chat-emp/shared';

/**
 * The auto reply, as the pane edits it.
 *
 * The DTO itself rather than a shape of its own: the draft is a copy of the
 * stored reply, and every field on it is a field that gets saved. A separate
 * client type would be two declarations of the same three things, kept in step
 * by hand.
 *
 * One reply for the whole ACCOUNT — not per posting, not per recruiter — so
 * there is no list to hold and nothing to select. Two states only: a message, or
 * a message with one form under it.
 */
export type AutoReply = AutoReplyDto;

/**
 * Whether this reply would actually go out.
 *
 * Enabled is not enough. An employer can switch the feature on and save an empty
 * message — the route accepts it deliberately — so "on" and "will send" are two
 * different questions, and the pane has to answer the second one to say anything
 * useful. The preview draws its empty state off this, and the panel warns off it.
 */
export function isSendableReply(reply: AutoReply): boolean {
  return reply.enabled && reply.message.trim() !== '';
}

/**
 * Whether two replies are the same reply, field by field.
 *
 * This is what `dirty` *is*: the draft is a copy of the stored reply, so having
 * changed something means differing from it — not having typed. Which is why
 * switching the toggle off and back on leaves Save disabled: nothing differs,
 * however much happened on the way.
 */
export function sameReply(a: AutoReply, b: AutoReply): boolean {
  return a.enabled === b.enabled && a.message === b.message && a.formId === b.formId;
}
